import React, { useState, useEffect } from 'react';
import { userSettingsAPI } from '../../services/api';
import './SettingsPage.css';

function PersonalDetails() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    firstname: '',
    surname: '',
    nickname: '',
    email: '',
    phone: '',
    dob: '',
    address_street_number: '',
    address_street_name: '',
    city: '',
    state: '',
    post_code: '',
  });

  useEffect(() => {
    loadPersonalDetails();
  }, []);

  // Convert DD/MM/YYYY to YYYY-MM-DD for date input
  const convertToDateInputFormat = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for backend
  const convertFromDateInputFormat = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  const loadPersonalDetails = async () => {
    try {
      setLoading(true);
      const response = await userSettingsAPI.getPersonalDetails();
      const userData = response.user || response;
      setFormData({
        firstname: userData.firstname || '',
        surname: userData.surname || '',
        nickname: userData.nickname || '',
        email: userData.email || '',
        phone: userData.phone || '',
        dob: convertToDateInputFormat(userData.dob) || '',
        address_street_number: userData.address_street_number || '',
        address_street_name: userData.address_street_name || '',
        city: userData.city || '',
        state: userData.state || '',
        post_code: userData.post_code || '',
      });
    } catch (error) {
      console.error('Failed to load personal details:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load personal details',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Convert date format before sending to backend
      const dataToSend = {
        ...formData,
        dob: convertFromDateInputFormat(formData.dob),
      };

      await userSettingsAPI.updatePersonalDetails(dataToSend);
      setMessage({
        type: 'success',
        text: 'Personal details updated successfully!',
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update personal details',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="settings-page-content">
      <h2>Personal Details</h2>
      <p className="page-description">
        Update your personal information and contact details.
      </p>

      {message.text && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstname">First Name *</label>
              <input
                type="text"
                id="firstname"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="surname">Last Name *</label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nickname">Nickname</label>
              <input
                type="text"
                id="nickname"
                name="nickname"
                value={formData.nickname}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="dob">Date of Birth</label>
              <input
                type="date"
                id="dob"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+61..."
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Address</h3>
          <div className="form-row address-row">
            <div className="form-group address-number">
              <label htmlFor="address_street_number">Number</label>
              <input
                type="text"
                id="address_street_number"
                name="address_street_number"
                value={formData.address_street_number}
                onChange={handleChange}
              />
            </div>
            <div className="form-group address-street">
              <label htmlFor="address_street_name">Street Name</label>
              <input
                type="text"
                id="address_street_name"
                name="address_street_name"
                value={formData.address_street_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row address-row">
            <div className="form-group address-city">
              <label htmlFor="city">City/Suburb</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
            <div className="form-group address-state">
              <label htmlFor="state">State</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="NSW">NSW</option>
                <option value="VIC">VIC</option>
                <option value="QLD">QLD</option>
                <option value="SA">SA</option>
                <option value="WA">WA</option>
                <option value="TAS">TAS</option>
                <option value="NT">NT</option>
                <option value="ACT">ACT</option>
              </select>
            </div>
            <div className="form-group address-postcode">
              <label htmlFor="post_code">Postcode</label>
              <input
                type="text"
                id="post_code"
                name="post_code"
                value={formData.post_code}
                onChange={handleChange}
                maxLength="4"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadPersonalDetails}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default PersonalDetails;
